import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/shared/lib"
import { Spinner } from "@/shared/ui/spinner"

/*
 * Verdant buttons. The variant names stay shadcn's — the vendored kit renders
 * `outline`, `ghost`, `secondary` and `destructive` internally (dialog footers,
 * pagination, sidebar, command) — but every tone, size and radius below comes
 * from the design system.
 *
 * Exactly one `default` (lime) button per screen. Sizes are the closed ladder:
 * 26 / 32 / 36 / 44, nothing between and nothing above.
 */
const buttonVariants = cva(
  // Two utilities from src/index.css carry the interaction. `press` is the
  // proportional scale plus a brightness dip, and it already excludes
  // menu-owners and disabled controls; the dip outranks any hover tint because
  // `:active` sorts after `:hover`. `lift` is the 1px rise on a real pointer,
  // which is what turns each variant's colour change into something the eye
  // catches — on the primary button it lands together with the glow widening
  // from 18% to 26%, and a button that rises into a bigger shadow reads as
  // rising in a way that `brightness-[1.08]` alone never did. Both bring their
  // own transition; `transition-all` here would only fight them.
  // Loading is not disabled: `data-loading` keeps the variant's own colour (the
  // grey disabled recipe applies only when actually unavailable).
  "group/button press lift inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-transparent bg-clip-padding whitespace-nowrap outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-primary/13 disabled:pointer-events-none disabled:not-data-loading:border-border-disabled disabled:not-data-loading:bg-surface-1 disabled:not-data-loading:text-fg-disabled disabled:not-data-loading:shadow-none aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-icon-md",
  {
    variants: {
      variant: {
        default:
          "bg-primary font-semibold text-primary-foreground shadow-primary-glow hover:shadow-primary-glow-strong hover:brightness-[1.08]",
        secondary:
          "border-border-strong bg-secondary font-medium text-secondary-foreground hover:bg-surface-4 aria-expanded:bg-surface-4",
        outline:
          "border-border bg-surface-1 font-medium text-foreground hover:border-border-strong hover:bg-surface-2 aria-expanded:bg-surface-2",
        ghost:
          "font-medium text-fg-secondary hover:bg-surface-2 hover:text-foreground aria-expanded:bg-surface-2 aria-expanded:text-foreground",
        destructive:
          "border-destructive/30 bg-destructive/12 font-medium text-destructive-text hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20",
        // Solid red is loud enough to be wrong outside a destructive modal.
        "destructive-solid":
          "bg-destructive font-semibold text-destructive-foreground hover:brightness-[1.06] focus-visible:border-destructive/40 focus-visible:ring-destructive/20",
        link: "font-medium text-primary underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-control-xs px-2.5 text-label [&_svg:not([class*='size-'])]:size-icon-xs",
        sm: "h-control-sm px-3 text-caption [&_svg:not([class*='size-'])]:size-icon-sm",
        default: "h-control-md px-4 text-body",
        lg: "h-control-lg px-5 text-h3 [&_svg:not([class*='size-'])]:size-icon-lg",
        icon: "size-control-md",
        "icon-xs":
          "size-control-xs [&_svg:not([class*='size-'])]:size-icon-xs",
        "icon-sm":
          "size-control-sm [&_svg:not([class*='size-'])]:size-icon-sm",
        "icon-lg":
          "size-control-lg [&_svg:not([class*='size-'])]:size-icon-lg",
      },
    },
    defaultVariants: {
      variant: "secondary",
      size: "default",
    },
  }
)

type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /**
     * Busy, not unavailable: the variant keeps its colour, a spinner appears,
     * input is blocked and `aria-busy` is announced. Use `disabled` only for
     * "this action does not apply", never for "this action is in flight".
     */
    isLoading?: boolean
  }

function Button({
  className,
  variant = "secondary",
  size = "default",
  isLoading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  return (
    <ButtonPrimitive
      data-slot="button"
      data-loading={isLoading || undefined}
      aria-busy={isLoading || undefined}
      disabled={isLoading || disabled}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {isLoading && <Spinner />}
      {children}
    </ButtonPrimitive>
  )
}

export { Button, buttonVariants }
